import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Truck, CheckCircle, Ban, PenLine, Shield, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SignaturePad = ({ onSave }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = 160;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    if (e.touches) return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const startDraw = (e) => { e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const pos = getPos(e); ctx.beginPath(); ctx.moveTo(pos.x, pos.y); setIsDrawing(true); setHasSignature(true); };
  const draw = (e) => { if (!isDrawing) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const pos = getPos(e); ctx.lineTo(pos.x, pos.y); ctx.stroke(); };
  const endDraw = () => setIsDrawing(false);
  const clear = () => { const c = canvasRef.current; const ctx = c.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height); setHasSignature(false); };
  const save = () => { if (!hasSignature) return; onSave(canvasRef.current.toDataURL('image/png')); };

  return (
    <div className="space-y-3" data-testid="public-signature-pad">
      <div className="border-2 border-dashed border-blue-200 rounded-xl overflow-hidden bg-white">
        <canvas ref={canvasRef} className="w-full cursor-crosshair" style={{ touchAction: 'none' }}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
          data-testid="public-signature-canvas" />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">Draw your signature above</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={clear}>Clear</Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} data-testid="public-apply-signature">
            <PenLine className="w-3.5 h-3.5 mr-1" />Apply Signature
          </Button>
        </div>
      </div>
    </div>
  );
};

const PublicInvoiceSign = () => {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signed, setSigned] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchInvoice = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/public/sign-invoice/${token}`);
      setInvoice(res.data);
      if (res.data.signed) setSigned(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Document not found or link expired');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const handleSign = async (signatureData) => {
    if (!signerName.trim()) { alert('Please enter your full legal name'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/sign-invoice/${token}`, {
        signer_name: signerName.trim(),
        signature_data: signatureData,
      });
      setSigned(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to sign document');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
      <div className="animate-spin w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <Ban className="w-14 h-14 text-rose-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Document Not Available</h2>
        <p className="text-slate-500">{error}</p>
      </div>
    </div>
  );

  if (signed) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4" data-testid="public-sign-success">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <CheckCircle className="w-10 h-10 text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Document Signed Successfully!</h2>
        <p className="text-slate-600 mb-6">Thank you for signing. A confirmation will be sent by Breamway Auto Transport.</p>
        <div className="bg-white rounded-xl border border-slate-200 p-5 text-left">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-3">Document Summary</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Reference</span><span className="font-mono font-bold text-slate-900">{invoice?.invoice_number}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-bold text-emerald-600">${(invoice?.total_price || 0).toLocaleString()}</span></div>
          </div>
        </div>
        <div className="mt-6">
          <a href="https://breamway.com" className="text-blue-600 text-sm font-medium hover:underline">Visit breamway.com</a>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-testid="public-sign-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 shadow-lg">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white text-lg tracking-tight">Breamway Auto Transport</h1>
              <p className="text-blue-300/70 text-xs">Shumail Technologies LLC</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-emerald-400 text-xs">
            <Shield className="w-4 h-4" />
            <span className="font-medium">Secure Document</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Document Header */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-blue-50/50 px-6 py-4 border-b border-blue-100">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-slate-900 text-lg">Transport Agreement & Invoice</h2>
              <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-bold uppercase">Awaiting Signature</span>
            </div>
            <p className="text-sm text-slate-500 mt-1">Reference: <span className="font-mono font-bold text-blue-600">{invoice.invoice_number}</span></p>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Customer</p>
                <p className="font-semibold text-slate-900">{invoice.customer_name}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Vehicle</p>
                <p className="font-semibold text-slate-900">{invoice.vehicle}{invoice.vehicle_body_type ? ` (${invoice.vehicle_body_type})` : ''}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 md:col-span-2">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Route</p>
                <p className="font-semibold text-slate-900">{invoice.route}</p>
              </div>
            </div>

            {/* Pricing */}
            <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden">
              <div className="grid grid-cols-4 bg-slate-50 text-center divide-x divide-slate-200">
                <div className="p-4">
                  <p className="text-xs text-slate-400 uppercase">Total</p>
                  <p className="text-lg font-bold text-emerald-600 mt-1">${(invoice.total_price || 0).toLocaleString()}</p>
                </div>
                <div className="p-4">
                  <p className="text-xs text-slate-400 uppercase">Deposit</p>
                  <p className="text-lg font-bold text-blue-600 mt-1">${(invoice.deposit_amount || 0).toLocaleString()}</p>
                </div>
                <div className="p-4">
                  <p className="text-xs text-slate-400 uppercase">COD Balance</p>
                  <p className="text-lg font-bold text-amber-600 mt-1">${(invoice.cod_amount || 0).toLocaleString()}</p>
                </div>
                <div className="p-4">
                  <p className="text-xs text-slate-400 uppercase">Payment</p>
                  <p className="text-lg font-bold text-slate-800 mt-1">{invoice.payment_method || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Terms */}
        {invoice.terms && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-slate-900 mb-3">Terms & Conditions</h3>
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-slate-600 text-sm max-h-72 overflow-y-auto pr-2 leading-relaxed">
              {invoice.terms}
            </div>
          </div>
        )}

        {/* Special Conditions */}
        {invoice.special_conditions && (
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-5">
            <h4 className="font-bold text-amber-800 text-sm mb-1">Special Conditions</h4>
            <p className="text-sm text-amber-700">{invoice.special_conditions}</p>
          </div>
        )}

        {/* Signature Section */}
        <div className="bg-white rounded-xl border-2 border-blue-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <PenLine className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900">Sign Document</h3>
              <p className="text-xs text-slate-500">By signing, you agree to the terms above</p>
            </div>
          </div>
          <div className="mb-4">
            <Label className="text-sm font-medium text-slate-700">Your Full Legal Name *</Label>
            <Input value={signerName} onChange={e => setSignerName(e.target.value)}
              placeholder="Enter your full name" className="mt-1" data-testid="public-signer-name" />
          </div>
          <SignaturePad onSave={handleSign} />
          {submitting && <p className="text-sm text-blue-600 mt-3 animate-pulse">Submitting signature...</p>}
        </div>

        {/* Footer */}
        <div className="text-center py-6 space-y-2">
          <div className="flex items-center justify-center gap-2 text-slate-400 text-xs">
            <Globe className="w-3.5 h-3.5" />
            <a href="https://breamway.com" className="hover:text-blue-600 transition-colors font-medium">breamway.com</a>
          </div>
          <p className="text-xs text-slate-400">Breamway Auto Transport | Shumail Technologies LLC</p>
          <p className="text-xs text-slate-400">USDOT# 4246498 | MC# 1622825</p>
        </div>
      </div>
    </div>
  );
};

export default PublicInvoiceSign;
